import { computed, Signal } from '@angular/core';
import { updateState } from '@ngrx-toolkit/core';
import {
  EmptyFeatureResult,
  SignalStoreFeature,
  signalStoreFeature,
  withComputed,
  withMethods,
  withState,
  WritableStateSource,
} from '@ngrx/signals';
import { capitalize } from './store-keys';

export type CallState =
  'init' | 'loadingOne' | 'loadedOne' | 'loadingMany' | 'loadedMany' | 'processing' | { error: string };

export type NamedCallStateSlice<Collection extends string> = {
  [K in Collection as `${K}CallState`]: CallState;
};

export type CallStateSlice = {
  callState: CallState;
};

export type NamedCallStateSignals<Prop extends string> = {
  [K in keyof CallStateSignals as `${Prop}${Capitalize<K>}`]: CallStateSignals[K];
};

type PendingMutations<Collection extends string> = {
  [K in Collection as `${K}PendingMutations`]: number;
};

export type CallStateSignals = {
  loadingOne: Signal<boolean>;
  loadedOne: Signal<boolean>;
  loadingMany: Signal<boolean>;
  loadedMany: Signal<boolean>;
  processing: Signal<boolean>;
  error: Signal<string | null>;
};

export type NamedCallStateMethods<Collection extends string, P extends boolean> = {
  [K in Collection as `update${Capitalize<K>}LoadedOne`]: (payload: P) => void;
} & {
  [K in Collection as `update${Capitalize<K>}LoadedMany`]: (payload: P) => void;
};

export type CallStateMethods<P extends boolean> = {
  updateLoadedOne: (payload: P) => void;
  updateLoadedMany: (payload: P) => void;
};

export function getCallStateKeys(config?: { collection?: string }) {
  const prop = config?.collection;
  return {
    callStateKey: prop ? `${prop}CallState` : 'callState',
    pendingMutationsKey: prop ? `${prop}PendingMutations` : 'pendingMutations',
    loadingManyKey: prop ? `${prop}LoadingMany` : 'loadingMany',
    loadingOneKey: prop ? `${prop}LoadingOne` : 'loadingOne',
    updateLoadedOneKey: prop ? `update${capitalize(prop)}LoadedOne` : 'updateLoadedOne',
    updateLoadedManyKey: prop ? `update${capitalize(prop)}LoadedMany` : 'updateLoadedMany',
    loadedOneKey: prop ? `${prop}LoadedOne` : 'loadedOne',
    loadedManyKey: prop ? `${prop}LoadedMany` : 'loadedMany',
    processingKey: prop ? `${prop}Processing` : 'processing',
    errorKey: prop ? `${prop}Error` : 'error',
  };
}

export function withCallState<Collection extends string, P extends boolean>(config: {
  collection: Collection;
}): SignalStoreFeature<
  EmptyFeatureResult,
  {
    state: NamedCallStateSlice<Collection> & PendingMutations<Collection>;
    props: NamedCallStateSignals<Collection>;
    methods: NamedCallStateMethods<Collection, P>;
  }
>;
export function withCallState<P extends boolean>(): SignalStoreFeature<
  EmptyFeatureResult,
  {
    state: CallStateSlice & { pendingMutations: number };
    props: CallStateSignals;
    methods: CallStateMethods<P>;
  }
>;
export function withCallState<Collection extends string>(config?: { collection: Collection }): SignalStoreFeature {
  const {
    callStateKey,
    pendingMutationsKey,
    errorKey,
    loadingOneKey,
    loadedOneKey,
    updateLoadedOneKey,
    loadingManyKey,
    loadedManyKey,
    updateLoadedManyKey,
    processingKey,
  } = getCallStateKeys(config);

  return signalStoreFeature(
    withState({ [callStateKey]: 'init', [pendingMutationsKey]: 0, [loadedOneKey]: false, [loadedManyKey]: false }),
    withComputed((state: Record<string, Signal<unknown>>) => {
      const callState = state[callStateKey] as Signal<CallState>;
      const pendingMutations = state[pendingMutationsKey] as Signal<number>;

      return {
        [loadingOneKey]: computed(() => callState() === 'loadingOne'),
        [loadingManyKey]: computed(() => callState() === 'loadingMany'),
        [processingKey]: computed(() => pendingMutations() > 0 || callState() === 'processing'),
        [errorKey]: computed(() => {
          const v = callState();
          return typeof v === 'object' ? v.error : null;
        }),
      };
    }),
    withMethods((store: WritableStateSource<object>) => {
      return {
        [updateLoadedOneKey]: (loaded: boolean): void => {
          updateState(store, updateLoadedOneKey, { [loadedOneKey]: loaded });
        },
        [updateLoadedManyKey]: (loaded: boolean): void => {
          updateState(store, updateLoadedManyKey, { [loadedManyKey]: loaded });
        },
      };
    }),
  );
}

type CallStatePatch<Prop extends string | undefined> = Prop extends string ? NamedCallStateSlice<Prop> : CallStateSlice;

function callStatePatch<Prop extends string | undefined>(state: CallState, prop?: Prop): CallStatePatch<Prop> {
  return { [getCallStateKeys({ collection: prop }).callStateKey]: state } as CallStatePatch<Prop>;
}

export function setLoadingOne<Prop extends string | undefined = undefined>(prop?: Prop): CallStatePatch<Prop> {
  return callStatePatch('loadingOne', prop);
}

export function setLoadedOne<Prop extends string | undefined = undefined>(prop?: Prop): CallStatePatch<Prop> {
  return callStatePatch('loadedOne', prop);
}

export function setLoadingMany<Prop extends string | undefined = undefined>(prop?: Prop): CallStatePatch<Prop> {
  return callStatePatch('loadingMany', prop);
}

export function setLoadedMany<Prop extends string | undefined = undefined>(prop?: Prop): CallStatePatch<Prop> {
  return callStatePatch('loadedMany', prop);
}

export function setProcessing<Prop extends string | undefined = undefined>(prop?: Prop): CallStatePatch<Prop> {
  return callStatePatch('processing', prop);
}

export function toErrorMessage(error: unknown): string {
  return !error ? '' : typeof error === 'object' && 'message' in error ? String(error.message) : String(error);
}

export function setError<Prop extends string | undefined = undefined>(
  error: unknown,
  prop?: Prop,
): CallStatePatch<Prop> {
  return callStatePatch({ error: toErrorMessage(error) }, prop);
}
